import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';

// STD-015: server state for the flight-details domain (shared dialog used by the
// Overview "Latest Flights" list, the Statistics timeline, and the Files tab).
// apiService is imported here (a *.queries.ts file) per STD-016.

export const flightDetailsKeys = {
  // NOTE: the tuple keeps the legacy ['statistics', 'flight-details', ...]
  // namespace verbatim so cache identity is unchanged after extracting this
  // module out of the statistics tab — existing invalidations still match.
  detail: (droneSerial: string, flightDate: string | null) =>
    ['statistics', 'flight-details', droneSerial, flightDate] as const,
};

// Flight details for a date — enabled only when a date is selected.
// The chart shows inline error state, so the global toast is suppressed.
export function useFlightDetailsQuery(droneSerial: string, flightDate: string | null) {
  return useQuery({
    queryKey: flightDetailsKeys.detail(droneSerial, flightDate),
    enabled: !!droneSerial && flightDate != null,
    meta: { skipGlobalErrorToast: true },
    queryFn: async () => {
      const response = await apiService.getFlightDetails(droneSerial, flightDate!);
      if (!response.success) throw new Error('Failed to load flight details');
      return response.flights;
    },
  });
}

// Imperative fetcher — called on user actions (clicking a flight card/tick, or a
// file's "Details" action). Not a hook; exported so components never import
// apiService directly (STD-016).
export async function fetchFlightDetails(droneSerial: string, flightDate: string) {
  return apiService.getFlightDetails(droneSerial, flightDate);
}
